import {
  faBars,
  faDownload,
  faEye,
  faFilter,
  faPaperclip,
  faPen,
  faRotateRight,
  faSearch,
  faTimeline,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Checkbox, Flex, Form, FormInstance, Input, Modal, Select, Space, Table, Tag, Tooltip } from "antd";
import { ColumnType, TableProps } from "antd/es/table";
import dayjs from "dayjs";
import { Key, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomizeTableModal from "../../../components/customize-table";
import FilterDrawer from "../../../components/filter-drawer";
import TableViewDropdown from "../../../components/table-view-dropdown";
import { APWorkflowStatusOptions, APWorkflowStatusTab, MatchedStatusEnum } from "../../../constants/ap-workflow";
import { DATE_FORMAT, DATE_FORMAT_DDMMYYYY_HHMMSS } from "../../../constants/common";
import { DEFAULT_PAGINATION } from "../../../constants/pagination";
import { PERMISSIONS } from "../../../constants/permission";
import { useMessageApi } from "../../../context/MessageContext";
import { usePageStore } from "../../../context/PageStoreContext";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useUpdateTableView } from "../../../hooks/useTableView";
import { useTableViews } from "../../../hooks/useTableViews";
import { useViewFile } from "../../../hooks/useViewFile";
import { IApWorkflowResult } from "../../../interfaces/ap-workflow";
import { IColumnConfig, ISortInfo } from "../../../interfaces/common";
import { ITableViewColumn, TableViewFilters } from "../../../interfaces/table-view";
import { ApWorkflowService } from "../../../services/ap-workflow";
import { SystemDefaultServices } from "../../../services/system-default";
import {
  countNonEmptyFields,
  debounce,
  formatDateFieldsFlat,
  formatEuropeanCurrencyIntl,
  formatNumberFixed,
  renderClassNameStatusApWorkflow,
} from "../../../utils/helper";
import ModalCreateBatch from "./create-batch";
import FilterApWorkflow from "./filter";
import { ERROR_CODES } from "../../../constants/enum";
import ModalConfirm from "../../../components/confirm";
interface TabDetailProps {
  type: string;
  status: string;
  suppliers: any[];
  apOwners: any[];
  masterData?: any;
}

interface ModalValidate {
  isShow: boolean;
  content: string;
  details: any;
  canCreateBatch: boolean;
  needToConfirm: boolean;
}

interface DetailErrorItem {
  id: string;
  imId: string;
  tab: string;
  supplierCode: string;
  status: string;
  bookingReference: string;
  leadPaxName: string;
  amount: string | number;
}

const renderFieldTable = (fieldName: string, value: any) => {
  const dateFields = ["sirReceivedDate", "espDueDate"];
  const datetimeFields = ["exportDatetime"];
  const numberFields = ["espAmount", "sirAmount"];

  if (dateFields.includes(fieldName)) {
    return value ? dayjs(value).format(DATE_FORMAT) : value;
  }

  if (datetimeFields.includes(fieldName)) {
    return value ? dayjs(value).format(DATE_FORMAT_DDMMYYYY_HHMMSS) : value;
  }

  if (numberFields.includes(fieldName)) {
    return <div style={{ textAlign: "right" }}>{value ? formatNumberFixed(value) : value}</div>;
  }

  if (fieldName === "flag") {
    return value && value.map((item: string) => <Tag key={item}>{item}</Tag>);
  }

  if (fieldName === "status") {
    return <span className={renderClassNameStatusApWorkflow(value)}>{value}</span>;
  }

  return value;
};

const TabDetail: React.FC<TabDetailProps> = ({ type, status, suppliers, apOwners, masterData }) => {
  const messageApi = useMessageApi();
  const { setPageValue, getPageValue } = usePageStore();
  const navigate = useNavigate();
  const hasPermissionEditor = useHasPermission([PERMISSIONS.WORKFLOW_EDITOR]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [changeStatusLoading, setChangeStatusLoading] = useState<boolean>(false);
  const [sortInfo, setSortInfo] = useState<ISortInfo>({});
  const [isForceCreateBatch, setIsForceCreateBatch] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalChangeStatus, setShowModalChangeStatus] = useState<boolean>(false);
  const [showModalCreateBatch, setShowModalCreateBatch] = useState<boolean>(false);
  const [modalValidateCreateBatch, setModalValidateCreateBatch] = useState<ModalValidate | null>(null);
  const [modalConfirm, setModalConfirm] = useState<any>(null);
  const [listApWorkflow, setListApWorkflow] = useState<IApWorkflowResult[] | any[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
  const [openFilter, setOpenFilter] = useState(false);
  const [isShowReview, setIsShowReview] = useState<boolean | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [childSorters, setChildSorters] = useState<Record<string, any>>({});
  const { loading: loadingFile, onViewFile: onDownloadAttachment, onDownloadBatchExport } = useViewFile();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });
  const TABLE_NAME = type === "Common" ? `app_workflow_${status}` : `flight_workflow_${status}`;

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedRowKeys([]);
    setListApWorkflow([]);
  }, [status]);

  useEffect(() => {
    if (TABLE_NAME) {
      refetchTableViews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TABLE_NAME]);

  useEffect(() => {
    const stored = getPageValue();
    if (stored?.searchValue) {
      setSearchValue(stored.searchValue);
      setDebouncedSearchValue(stored.searchValue);
    }

    if (stored?.pagination) {
      setPagination(stored.pagination);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    dataTableView,
    loading: loadingTableView,
    setDataTableView,
    refetchTableViews,
  } = useTableViews({
    tableName: TABLE_NAME,
    shouldFetch: false,
  });

  const { updateTableView } = useUpdateTableView();

  const currentTableView = dataTableView?.currentView!;
  const { filters = undefined, columns: columnConfigs = [] } = currentTableView || {};
  const isDefaultView = !!dataTableView?.defaultView;

  // check is show reivew config to remove filter saved
  const removeFilterReview = () => {
    const cleanedFilters = { ...filters };

    if (!isShowReview && "isReviewed" in cleanedFilters) {
      delete cleanedFilters.isReviewed;
    }

    return cleanedFilters;
  };
  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        keyword: debouncedSearchValue?.trim() || undefined,
        tab: status,
        type,
        ...formatDateFieldsFlat(removeFilterReview()),
        page: pagination?.current ?? 1,
        pageSize: pagination?.pageSize ?? 10,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      };

      const response = await ApWorkflowService.getAllAndRow(params);

      setListApWorkflow(response.data.results);

      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
    } catch (error) {
      console.error("[fetchData] Error fetching AP Workflows:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemDefaultConfig = async () => {
    if (status !== APWorkflowStatusTab.MATCHED) return;
    try {
      setLoading(true);
      const res = await SystemDefaultServices.getDefaultTermsRule({ type: "GENERAL_SETTING" });
      if (res?.data) {
        setIsShowReview(!!res.data.isShowReview);
      }
    } catch (error) {
      console.error("[fetchData] Error Fetching default system:", error);
      setIsShowReview(false);
    } finally {
      setLoading(false);
    }
  };

  const activeFilterCount = countNonEmptyFields(removeFilterReview());

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    // Handle pagination
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.pageSize !== prev.pageSize ? 1 : paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));

    // Handle sorting
    if (sorter?.field) {
      setSortInfo({
        orderBy: sorter.field,
        orderDirection: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      // Clear sorting
      setSortInfo({});
    }
  };

  useEffect(() => {
    const run = async () => {
      if (loadingTableView) return;

      if (isShowReview === null) {
        await fetchSystemDefaultConfig();
      }

      if (isShowReview !== null || status !== APWorkflowStatusTab.MATCHED) {
        await fetchData();
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadingTableView,
    // status,
    isShowReview,
    debouncedSearchValue,
    filters,
    pagination.current,
    pagination.pageSize,
    sortInfo,
  ]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
      }, 1000),
    []
  );

  const checkRecordTabReview = async (id: string, check: boolean) => {
    try {
      setLoading(true);
      const params = { isReview: check };

      const response = await ApWorkflowService.checkTabReview(id, params);
      const { success } = response;

      if (success) {
        const invoicesIdsUpdated = response.data.invoicesIdsUpdated;
        setListApWorkflow((prev) =>
          prev.map((item) => {
            if (invoicesIdsUpdated.includes(Number(item.id))) {
              return {
                ...item,
                isReviewed: check,
              };
            }
            return item;
          })
        );
        messageApi.success(`Changed review status successfully`);
      }
    } catch (error) {
      console.error("[Check/UnCheck] Error Checking/UnCheck AP Workflow record:", error);
    } finally {
      setLoading(false);
    }
  };
  const colError: ColumnType<IApWorkflowResult> = {
    title: "",
    key: "error",
    width: 40,
    fixed: "left",
    align: "center",
    render: (_: any, record: IApWorkflowResult) => <>{contentError(record)}</>,
  };

  const contentError = (record: IApWorkflowResult) => {
    const { errors } = record;
    
    if (!errors?.length) return null;
    const content = () => (
      <ul style={{ margin: "0", padding: "5px  5px 5px 25px" }}>
        {errors.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
    return (
      <Tooltip title={content()} placement="right">
        <FontAwesomeIcon icon={faTriangleExclamation} className="text-warning" />
      </Tooltip>
    );
  };
  
  const generateColumns = (config: ITableViewColumn[]): ColumnType<IApWorkflowResult>[] =>
    config
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .filter((c) => {
        if (c.column === "isReviewed") {
          return c.isDisplay && isShowReview;
        } else {
          return c.isDisplay;
        }
      })
      .map((col) =>
        col.children && col.children.length > 0
          ? {
              title: col.label,
              width: 150,
              children: generateColumns(col.children),
            }
          : {
              title: col.label,
              dataIndex: col.column,
              key: col.column,
              sorter: true,
              align: col.column === "isReviewed" ? "center" : "left",
              sortDirections: ["ascend", "descend"],
              width: 150,
              render:
                col.column === "isReviewed"
                  ? (_: any, record: IApWorkflowResult) => (
                      <Checkbox
                        checked={!!record.isReviewed}
                        onChange={(e) => checkRecordTabReview(record.id, e.target.checked)}
                      />
                    )
                  : (value: any) => renderFieldTable(col.column, value),
            }
      );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => generateColumns(columnConfigs), [columnConfigs, isShowReview]);

  const colActive: ColumnType<IApWorkflowResult> = {
    title: "Actions",
    key: "action",
    width: type === "Common" && status === "completed" ? 160 : 120,
    fixed: "right",
    align: "center",
    render: (_: any, record: IApWorkflowResult) => {
      const attachment = record.attachments?.[0];
      return (
        <Space size="small" className="box-action" align="center">
          {status === "completed" && (
            <>
              <Tooltip title={record?.batchName}>
              <Button
                type="link"
                size="small"
                icon={<FontAwesomeIcon icon={faDownload} />}
                disabled={!record?.batchId || !record?.batchName}
                onClick={(e) => {
                  e.stopPropagation();
                  onDownloadBatchExport?.(Number(record?.batchId), record?.batchName!);
                }}
              />
              </Tooltip>
              <div>|</div>
            </>
          )}

          {type === "Common" && (
            <>
              <Tooltip title={attachment?.fileName}>
                <Button
                  type="link"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownloadAttachment?.(Number(attachment?.invoiceId), attachment?.id!, attachment?.fileName!);
                  }}
                  disabled={!attachment}
                >
                  <FontAwesomeIcon icon={faPaperclip} />
                </Button>
              </Tooltip>
              <div>|</div>
            </>
          )}

          <Button type="link" size="small" onClick={() => navigateDetail(record.id)}>
            <FontAwesomeIcon icon={hasPermissionEditor ? faPen : faEye} />
          </Button>
          <div>|</div>
          <Button type="link" size="small" onClick={() => linkToAudit(record.id)}>
            <FontAwesomeIcon icon={faTimeline} />
          </Button>
        </Space>
      );
    },
  };

  const navigateDetail = (id: string) => {
    setPageValue({
      searchValue,
      pagination,
      currentStatus: status,
    });
    const detailLink = type === "Flight" ? `/flight-workflow/${id}` : `/ap-workflow/${id}`;
    navigate(detailLink);
  };

  const rowSelection: TableProps<IApWorkflowResult>["rowSelection"] = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], selectedRows: IApWorkflowResult[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record: IApWorkflowResult) => ({
      id: record.id,
    }),
    preserveSelectedRowKeys: true,
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  //MARK: reload function
  const handleReload = async () => {
    setSearchValue("");
    setDebouncedSearchValue("");
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (status === APWorkflowStatusTab.MATCHED) {
      await fetchSystemDefaultConfig();
    }
    await fetchData();
  };

  const handleApplyFilters = (values: TableViewFilters) => {
    const newFilters = { ...filters, ...values };
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, filters: newFilters } });
    if (isDefaultView) {
      updateTableView({
        tableName: TABLE_NAME,
        filters: newFilters,
      });
    }
    setDebouncedSearchValue("");
    setSearchValue("");
    setOpenFilter(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSaveCustomizeTable = async (configs: IColumnConfig[]) => {
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, columns: configs } });
  };

  //MARK: handle change status tab review
  const handleChangeStatusTabReview = async (ids: Key[], newStatus: string | null) => {
    if (!newStatus) {
      return;
    }
    try {
      const response = await ApWorkflowService.changeStatus(ids, newStatus, true);

      const { success, data } = response;
      if (success) {
        const { countUpdated, countFailed, successIds } = data;
        messageApi.success(
          `Successfully moved to ${newStatus} for (${countUpdated}) rows, (${countFailed}) failed to move`,
          10
        );
        // Filter selected keys: keep only failed IDs
        setSelectedRowKeys((prev) => prev.filter((id) => !successIds.includes(id)));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setShowModalChangeStatus(false);
      handleReload();
    }
  };

  const handleCancelModalChangeStatus = (form: FormInstance<any>) => {
    setShowModalChangeStatus(false);
    form.resetFields();
  };

  const handleChangeStatus = async (newStatus: string | null, isForce: boolean = false, tab?: string) => {
    if (!newStatus) {
      return;
    }
    try {
      setChangeStatusLoading(true);
      const response = await ApWorkflowService.changeStatus(selectedRowKeys, newStatus, isForce, tab);
      const { success } = response;
      if (success) {
        const message =
          newStatus === MatchedStatusEnum.MATCHED
            ? "Matching rows reverted successfully"
            : "Matching rows are now ready to export";
        messageApi.success(message);
        setModalConfirm(null);
        setSelectedRowKeys([]);
        handleReload();
      }
    } catch (error: any) {
      console.error(error);
      if (error.errorCode === ERROR_CODES.SIR_FULL_ERROR) {
        setShowModalCreateBatch(false);
        setModalConfirm({
          isOpen: true,
          title: "Change Status",
          description: error.message,
          onConfirm: () => handleChangeStatus(newStatus, true),
          details: error.details?.map((item: any) => ({
            id: item.id,
            imId: item.imId,
            supplierCode: item.supplierCode,
            tab: item.tab,
            status: item.status,
            bookingReference: item.bookingRef,
            leadPaxName: item.leadPax,
            amount: item.amount,
          })),
          isProceed: false
        });
      } else {
        messageApi.error(error.message || "Create batch failed");
      }
    } finally {
      setChangeStatusLoading(false);
    }
  };

  const handleCreateBatch = async (name?: string, isForce?: boolean) => {
    try {
      isForce = isForce || isForceCreateBatch;
      const response = await ApWorkflowService.createBatch({ type, ids: selectedRowKeys, name, isForce });
      const { success } = response;
      if (success) {
        messageApi.success("Batch created successfully");
        setShowModalCreateBatch(false);
        setModalValidateCreateBatch(null);
        setModalConfirm(null);
        setSelectedRowKeys([]);
        handleReload();
      }
    } catch (error: any) {
      if (error.errorCode === ERROR_CODES.SIR_FULL_ERROR) {
        setShowModalCreateBatch(false);
        setModalConfirm({
          isOpen: true,
          title: "Confirm Create Batch",
          description: `At least 1 invoice in this batch has SIRs that have not been selected, do you still want to proceed posting?`,
          onConfirm: () => handleCreateBatch(name, true),
        });
      } else {
        messageApi.error(error.message || "Create batch failed");
      }
    }
  };

  const handleChangeBatchStatus = async () => {
    try {
      setChangeStatusLoading(true);
      const response = await ApWorkflowService.changeBatchStatus(selectedRowKeys, type);
      const { success } = response;
      if (success) {
        messageApi.success("Batch is now completed");
        setShowModalCreateBatch(false);
        setSelectedRowKeys([]);
        handleReload();
      }
    } catch (error: any) {
      messageApi.error(error.message || "Change batch status failed");
    } finally {
      setChangeStatusLoading(false);
    }
  };

  const renderModalValidateCreateBatch = () => (
    <Modal
      width={"620px"}
      open={modalValidateCreateBatch?.isShow}
      onCancel={() => setModalValidateCreateBatch(null)}
      onOk={() => {
        setShowModalCreateBatch(true);
      }}
      okButtonProps={{
        style: { display: modalValidateCreateBatch?.needToConfirm ? "inline-block" : "none" },
      }}
      title={"Create Batch"}
      okText="Proceed"
    >
      <span style={{ color: !modalValidateCreateBatch?.canCreateBatch ? "#ff4d4f" : "" }}>
        {modalValidateCreateBatch?.content}
      </span>
      <ConfirmDetailList details={modalValidateCreateBatch?.details} />
    </Modal>
  );

  const validateCreateBatch = async () => {
    try {
      const response = await ApWorkflowService.validateCreateBatch(selectedRowKeys);

      // If has permission to create batch and doesn't need to confirm then display modal create batch.
      if (response.data?.canCreateBatch && !response.data?.needToConfirm) {
        setShowModalCreateBatch(true);
        return;
      }

      setModalValidateCreateBatch({
        isShow: true,
        content: response.data?.message,
        details: response.data?.details?.map((item: any) => ({
          imId: item.imId,
          supplierCode: item.supplierCode,
          tab: item.tab,
          status: item.status,
          bookingReference: item.bookingRef,
          leadPaxName: item.leadPax,
          amount: item.amount,
        })),
        canCreateBatch: response.data?.canCreateBatch,
        needToConfirm: response.data?.needToConfirm,
      });
      setIsForceCreateBatch(response.data?.canCreateBatch);
    } catch (error: any) {
      messageApi.error(error.message || "Create batch failed");
    }
  };

  const filteredColumnsForCustomize = useMemo(
    () =>
      columnConfigs
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .filter((item) => (item.column === "isReviewed" ? !!isShowReview : true)),
    [columnConfigs, isShowReview]
  );

  const linkToAudit = (link: string) => {
    setPageValueApWorkflow();
    const detailLink = type === "Flight" ? `/flight-workflow/${link}/audit-trail` : `/ap-workflow/${link}/audit-trail`;
    navigate(detailLink);
  };

  const setPageValueApWorkflow = () => {
    setPageValue({
      searchValue,
      pagination,
    });
  };

  const renderActionButtons = () => {
    if (!hasPermissionEditor) return null;
    if (selectedRowKeys.length === 0) return null;

    switch (status) {
      case APWorkflowStatusTab.REVIEW:
        return (
          <Button type="primary" onClick={() => setShowModalChangeStatus(true)}>
            {`Change Status for ${selectedRowKeys.length} selected items`}
          </Button>
        );
      case APWorkflowStatusTab.MATCHED:
        return (
          <Button
            type="primary"
            onClick={() => handleChangeStatus(MatchedStatusEnum.READY_TO_EXPORT, false, "Matched")}
            loading={changeStatusLoading}
          >
            {`Ready to Export for ${selectedRowKeys.length} selected items`}
          </Button>
        );
      case APWorkflowStatusTab.READY_TO_EXPORT:
        return (
          <>
            <Button
              type="primary"
              onClick={() => handleChangeStatus(MatchedStatusEnum.MATCHED, false, "Ready to Export")}
              loading={changeStatusLoading}
            >
              {`Move to Matched for ${selectedRowKeys.length} selected items`}
            </Button>
            <Button
              type="primary"
              onClick={() => (type === "Flight" ? setShowModalCreateBatch(true) : validateCreateBatch())}
              style={{ marginLeft: 8 }}
            >
              {`Post to Finance for ${selectedRowKeys.length} selected items`}
            </Button>
            {renderModalValidateCreateBatch()}
          </>
        );
      case APWorkflowStatusTab.EXPORTED:
        return (
          <Button type="primary" onClick={() => handleChangeBatchStatus()} loading={changeStatusLoading}>
            {`Mark Completed for ${selectedRowKeys.length} selected items`}
          </Button>
        );
      default:
        return null;
    }
  };

  const batchColumns = [
    {
      title: "Batch Name",
      dataIndex: "batchName",
      key: "batchName",
    },
    {
      title: "Exported Datetime",
      dataIndex: "exportedDatetime",
      key: "exportedDatetime",
      render: (value: any) => (value ? dayjs(value).format(DATE_FORMAT_DDMMYYYY_HHMMSS) : ""),
    },
    {
      title: "SIR Matching Records Total",
      dataIndex: "sirMatchingRecordsTotal",
      key: "sirMatchingRecordsTotal",
      render: (value: any) => value ?? 0,
    },
    {
      title: "Action",
      key: "operation",
      render: (_: any, record: any) => (
        <Button
          type="link"
          icon={<FontAwesomeIcon icon={faDownload} />}
          onClick={(e) => {
            e.stopPropagation();
            onDownloadBatchExport?.(Number(record?.batchId), record?.batchName);
          }}
        />
      ),
    },
  ];

  const ConfirmDetailList: React.FC<{ details: DetailErrorItem[] }> = ({ details }) => (
    <div
      style={{
        maxHeight: 180,
        overflowY: "auto",
        marginTop: 8,
        paddingRight: 8,
      }}
    >
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {details?.map((item, idx) => (
          <li key={idx} style={{ marginBottom: 4 }}>
            <b>{item.status}</b> ({item.tab}) - {item.supplierCode} {item.bookingReference ? "-" : ""} {item.bookingReference} {item.leadPaxName ? "-": ""} {item.leadPaxName} -{" "}
            {formatEuropeanCurrencyIntl(item.amount)}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <>
      <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Flex gap={12} align="center">
          <Input
            value={searchValue}
            onChange={handleInputChange}
            prefix={<FontAwesomeIcon icon={faSearch} style={{ color: "var(--text-blue-500)" }} />}
            placeholder="Enter Booking Ref., Supp. Code, Supp. Name or Lead Pax Name"
            style={{ width: 600 }}
            size="large"
            allowClear
          />

          {renderActionButtons()}
        </Flex>

        <Flex gap={12}>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => setShowModal(true)}
            loading={loading || loadingTableView}
          >
            <FontAwesomeIcon icon={faBars} style={{ fontSize: "16px" }} />
            Customise
          </Button>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => setOpenFilter(true)}
            loading={loading || loadingTableView}
          >
            <FontAwesomeIcon icon={faFilter} style={{ fontSize: "16px" }} />
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>
          <TableViewDropdown
            tableName={TABLE_NAME}
            dataTableView={dataTableView!}
            onTrigger={() => refetchTableViews()}
          />
          <Button color="primary" variant="outlined" onClick={handleReload} loading={loading || loadingTableView}>
            <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: "16px" }} />
            Reload
          </Button>
        </Flex>
      </Flex>
      <Table
        rowKey="id"
        rowSelection={status !== APWorkflowStatusTab.COMPLETED ? rowSelection : undefined}
        dataSource={listApWorkflow}
        columns={status === APWorkflowStatusTab.MATCHED || status === APWorkflowStatusTab.REVIEW ? [colError, ...columns, colActive] : status !== APWorkflowStatusTab.EXPORTED ? [...columns, colActive] : batchColumns}
        expandable={
          status !== APWorkflowStatusTab.EXPORTED
            ? undefined
            : {
                expandedRowRender: (item) => {
                  const sorter = childSorters[item.id];
                  let sortedData = item?.sirs || [];
                  if (sorter && sorter.field && sorter.order) {
                    sortedData = [...sortedData].sort((a, b) => {
                      const aValue = a[sorter.field];
                      const bValue = b[sorter.field];
                      if (typeof aValue === "string" && typeof bValue === "string") {
                        const result = aValue.localeCompare(bValue);
                        return sorter.order === "ascend" ? result : -result;
                      }
                      if (typeof aValue === "number" && typeof bValue === "number") {
                        const result = aValue - bValue;
                        return sorter.order === "ascend" ? result : -result;
                      }
                      return 0;
                    });
                  }

                  return (
                    <Table
                      rowKey="id"
                      columns={[...columns, colActive]}
                      dataSource={sortedData}
                      pagination={false}
                      bordered
                      size="small"
                      className="table-fm"
                      rowClassName={(_, index) => (index % 2 === 0 ? "highlight-row" : "")}
                      scroll={{ x: 1200 }}
                      loading={loading || loadingFile || loadingTableView}
                      onChange={(_, __, sorter) => {
                        setChildSorters((prev) => ({
                          ...prev,
                          [item.id]: sorter,
                        }));
                      }}
                    />
                  );
                },
              }
        }
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        bordered
        size="small"
        className="table-fm"
        rowClassName={(_, index) => {
          if (_?.isGrouped) {
            return _?.isParent ? "highlight-parent-row" : "highlight-group-row";
          }
          return index % 2 === 0 ? "highlight-row" : "";
        }}
        scroll={{ x: 1200 }}
        loading={loading || loadingFile || loadingTableView}
      />
      {openFilter && (
        <FilterDrawer
          title="Filter AP Workflow"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          onApply={handleApplyFilters}
          values={filters}
          children={
            <FilterApWorkflow
              suppliers={suppliers}
              apOwners={apOwners}
              status={status}
              isShowReview={isShowReview ?? false}
              masterData={masterData}
            />
          }
        />
      )}
      {showModal && (
        <CustomizeTableModal
          open={showModal}
          onClose={() => setShowModal(false)}
          configsValue={filteredColumnsForCustomize}
          onSave={handleSaveCustomizeTable}
          tableName={TABLE_NAME}
          isSave={isDefaultView}
        />
      )}
      {showModalChangeStatus && (
        <Modal
          open={showModalChangeStatus}
          title="Confirm Update"
          okText="Confirm"
          onCancel={() => handleCancelModalChangeStatus(form)}
          onOk={() => {
            form
              .validateFields()
              .then((values) => {
                handleChangeStatusTabReview(selectedRowKeys, values.status);
                form.resetFields();
              })
              .catch(() => {
                // validation failed, do nothing
              });
          }}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="status"
              label="Please select new status for the selected items"
              rules={[{ required: true, message: "Please select a status" }]}
            >
              <Select
                style={{ width: "100%" }}
                allowClear
                showSearch
                placeholder="Select status"
                options={APWorkflowStatusOptions}
              />
            </Form.Item>
          </Form>
        </Modal>
      )}

      {showModalCreateBatch && (
        <ModalCreateBatch
          open={showModalCreateBatch}
          onCancel={() => {
            setShowModalCreateBatch(false);
          }}
          onCreate={handleCreateBatch}
        />
      )}
      {modalConfirm?.isOpen && (
        <ModalConfirm
          width={620}
          title={modalConfirm.title}
          description={modalConfirm.description}
          open={modalConfirm.isOpen}
          onCancel={() => setModalConfirm(null)}
          onOk={modalConfirm.onConfirm}
          detail={<ConfirmDetailList details={modalConfirm.details}
          />}
          isProceed={modalConfirm.isProceed}
        />
      )}
    </>
  );
};

export default TabDetail;
